/* Reading the tenant's registered connections. **The one definition, at the entity layer.**
 *
 * This is the first data hook in `entities/`, and it is here for a specific reason rather than as a
 * new habit. Three elements need to name a connection — the Connections screen, the connection
 * detail page, and the dataset catalogue, which groups its rows by source — and the layer rules
 * (`app -> features -> entities -> shared`) let a feature import an entity but give two features no
 * way to share without one depending on the other.
 *
 * When `catalog-browser` needed this, the shortest path was importing `@/features/connections`, the
 * way `HomeScreen` already does. That would have closed a cycle: `connections` exports
 * `ConnectionDetail`, which renders `CatalogBrowser`. ESLint has no `import/no-cycle` rule, so
 * nothing would have failed — the two barrels would simply have begun initialising each other, and
 * the failure that eventually surfaces from that is an `undefined` component at render time in
 * whichever screen happens to be evaluated first. Moving the *read* below both features makes the
 * cycle unrepresentable instead of merely absent today.
 *
 * The **mutations stay in `features/connections`** — registering, verifying and harvesting are
 * things a user does to a source, which is feature work. They invalidate `CONNECTIONS_QUERY_KEY`
 * from here, so there is still exactly one cache entry for "which connections exist" and no screen
 * can hold a different answer from another.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { isConnection, isConnectionSummary } from '@/shared/api/generated/catalog';
import type { Connection, ConnectionSummary } from '@/shared/api/generated/catalog';
import { getPage } from '@/shared/api/http';
import type { Page } from '@/shared/api/http';

export const CONNECTIONS_URL = '/v1/catalog/connections';

/** The one cache key for the connection list. Exported because the feature's mutations invalidate
 * it — two keys for one endpoint is two answers to "which connections exist". */
export const CONNECTIONS_QUERY_KEY = ['catalog', 'connections'] as const;

/** How many sources the catalogue grid and the connections table read. The route's maximum is 200.
 *
 * 50 because both surfaces are lists a person scans, and a deployment with more than fifty sources
 * needs paging rather than a longer page. A constant so a screen can say "the first 50" honestly
 * rather than implying it has them all.
 */
export const SOURCES_PER_PAGE = 50;

/** Per-source dataset counts, scored counts, harvest recency and live harvest progress.
 *
 * **One request for the whole grid**, which is the entire reason it exists as its own route: a
 * screen that renders one row per source and then asks per row is the N+1 that looks fine on a demo
 * tenant with one source.
 *
 * **At the entity layer for the reason `useConnections` is**, and it moved here when the connections
 * table needed the harvest progress the catalogue grid was already reading. `features/connections`
 * importing `features/catalog-browser` would have closed a cycle — `connections` exports
 * `ConnectionDetail`, which renders `CatalogBrowser` — and ESLint has no `import/no-cycle` rule, so
 * nothing would have failed except two barrels initialising each other. Moving the *read* below both
 * features makes that unrepresentable rather than merely absent today.
 *
 * **Nothing here fabricates a zero.** `scored`, `last_harvest_at` and `harvest_state` are nullable on
 * the wire and stay nullable, so every consumer has to decide what "we did not measure this" reads
 * as.
 */
export function useConnectionSummaries(): UseQueryResult<Page<ConnectionSummary>> {
  return useQuery({
    queryKey: ['catalog', 'connection-summaries', SOURCES_PER_PAGE],
    queryFn: () =>
      getPage(
        `/v1/catalog/connections/summary?limit=${String(SOURCES_PER_PAGE)}`,
        isConnectionSummary,
        'connection summary',
      ),
    retry: false,
  });
}

export function useConnections(): UseQueryResult<Page<Connection>> {
  return useQuery({
    queryKey: CONNECTIONS_QUERY_KEY,
    queryFn: () => getPage(CONNECTIONS_URL, isConnection, 'connection'),
    retry: false,
  });
}

/** One connection by id, selected out of the same list read.
 *
 * A `select` on the shared query rather than a `GET /connections/{id}`, so a detail page issues no
 * request the list has not already made and two components asking "which connections exist" can
 * never disagree about the answer.
 *
 * **`data === undefined` after a successful read means the id was not among the fetched
 * connections** — a bad link, a retired source, or (rare, at up to 200) one past the cap. It never
 * means "still loading"; that is `isPending`, as for any other query. Three screens render three
 * different sentences off that distinction, which is why it is stated here rather than left to be
 * rediscovered.
 *
 * Moved down from `features/connections` when `catalog-browser` needed it: the source-datasets
 * screen names the connection in its breadcrumb, and one feature may not import another's
 * internals. The same reasoning `useConnections` carries above.
 */
export function useConnection(
  connectionId: string,
): UseQueryResult<Connection | undefined> {
  return useQuery({
    queryKey: CONNECTIONS_QUERY_KEY,
    queryFn: () => getPage(CONNECTIONS_URL, isConnection, 'connection'),
    select: (page) => page.items.find((connection) => connection.id === connectionId),
    retry: false,
  });
}

/** Ids to names, for a screen that holds a `connection_id` and owes the reader a name.
 *
 * A `select` on the same query rather than a second one, so the catalogue's grouping and the
 * Connections screen's cards are the same fetch and cannot disagree.
 *
 * **An id absent from this map is not "no such connection".** The list route is capped at 200 with
 * no cursor, so past that a real connection is simply not here — which is why `groupByConnection`
 * distinguishes an unnamed group from a named one rather than falling back to rendering the id.
 */
export function useConnectionNames(): UseQueryResult<ReadonlyMap<string, string>> {
  return useQuery({
    queryKey: CONNECTIONS_QUERY_KEY,
    queryFn: () => getPage(CONNECTIONS_URL, isConnection, 'connection'),
    select: (page) =>
      new Map(page.items.map((connection) => [connection.id, connection.name])),
    retry: false,
  });
}
